import { KeyboardEvent, useEffect, useState } from 'react';

import { CustomerStory } from '@/content/customerStories/customerStories.types';

import {
	ContentFacetContext,
	CustomerStoryFilterObject,
	FilterCategories,
	FilterOptions,
	StoryGrid,
	StoryGridItem,
	SubFilterObject,
} from './types';

const sizeMap = {
	lg: 4,
	md: 3,
	sm: 2,
};

// This uses traditional while loops
// so that we can break out of the loop
export const areFiltersAreActive = (filterObject: CustomerStoryFilterObject): boolean => {
	let isActive = false;
	const filterCategories = Object.values(filterObject);
	let i = 0;
	while (!isActive && i < filterCategories.length) {
		const filterCategoryValues = filterCategories[i] as SubFilterObject;
		let j = 0;
		while (!isActive && j < Object.values(filterCategoryValues).length) {
			const subFilterObject = Object.values(filterCategoryValues)[j] as FilterOptions;
			if (subFilterObject['committedActive']) {
				isActive = true;
			}
			j++;
		}
		i++;
	}
	return isActive;
};

export const commitAllStagedActiveValues = (
	prevState: CustomerStoryFilterObject,
): CustomerStoryFilterObject => {
	return Object.keys(prevState).reduce((acc, filterCategory) => {
		acc[filterCategory as FilterCategories] = {
			...prevState[filterCategory as FilterCategories],
			...Object.entries(prevState[filterCategory as FilterCategories]).reduce(
				(acc: SubFilterObject, [filterOptionName, filterOptionValue]) => {
					acc[filterOptionName] = {
						...filterOptionValue,
						committedActive: filterOptionValue.stagedActive,
					};
					return acc;
				},
				{},
			),
		};
		return acc;
	}, {} as CustomerStoryFilterObject);
};

export const setAllStagedActiveToFalse = (
	prevState: CustomerStoryFilterObject,
): CustomerStoryFilterObject => {
	return Object.keys(prevState).reduce((acc, filterCategory) => {
		acc[filterCategory as FilterCategories] = {
			...prevState[filterCategory as FilterCategories],
			...Object.entries(prevState[filterCategory as FilterCategories]).reduce(
				(acc: SubFilterObject, [filterOptionName, filterOptionValue]) => {
					acc[filterOptionName] = {
						...filterOptionValue,
						stagedActive: false,
					};
					return acc;
				},
				{},
			),
		};
		return acc;
	}, {} as CustomerStoryFilterObject);
};

export const resetAllStagedActiveToCommittedActiveValue = (
	prevState: CustomerStoryFilterObject,
): CustomerStoryFilterObject => {
	return Object.keys(prevState).reduce((acc, filterCategory) => {
		acc[filterCategory as FilterCategories] = {
			...prevState[filterCategory as FilterCategories],
			...Object.entries(prevState[filterCategory as FilterCategories]).reduce(
				(acc: SubFilterObject, [filterOptionName, filterOptionValue]) => {
					acc[filterOptionName] = {
						...filterOptionValue,
						stagedActive: filterOptionValue.committedActive,
					};
					return acc;
				},
				{},
			),
		};
		return acc;
	}, {} as CustomerStoryFilterObject);
};

export const handleKeyDownButtonComplexFilterModal = (
	event: KeyboardEvent,
	filter: string,
	onClickHandler: (filter: string) => void,
) => {
	if (event.key === 'Enter') {
		onClickHandler(filter);
	}
};

export const getActiveFiltersByFilterType = (
	filter: FilterCategories | 'all',
	filterObject: CustomerStoryFilterObject,
): number => {
	if (filter === 'all') {
		return Object.values(filterObject).reduce(
			(acc: number, filterCategory: SubFilterObject) =>
				acc +
				Object.values(filterCategory).reduce(
					(subacc: number, filterOption: FilterOptions) =>
						filterOption.committedActive ? subacc + 1 : subacc,
					0,
				),
			0,
		);
	} else {
		return Object.values(filterObject[filter]).reduce(
			(acc: number, subFilter: FilterOptions) => (subFilter.committedActive ? acc + 1 : acc),
			0,
		);
	}
};

const NUMBER_OF_FALLBACK_STORIES = 3;

export const handleFallbackCustomerStories = (
	customerStories: CustomerStory[],
): CustomerStory[] => {
	const fallbackCustomerStoryList: CustomerStory[] = new Array(NUMBER_OF_FALLBACK_STORIES);

	const extraFallbackCustomerStoryList: CustomerStory[] = [];

	customerStories.forEach((story) => {
		switch (story.urlSlug) {
			case 'the-very-group': {
				fallbackCustomerStoryList[0] = story;
				break;
			}

			case 'sonymusic-publishing': {
				fallbackCustomerStoryList[1] = story;
				break;
			}

			case 'lumen': {
				fallbackCustomerStoryList[2] = story;
				break;
			}

			case 'edenred':
			case 'engie':
			case 'sunlife': {
				extraFallbackCustomerStoryList.push(story);
				break;
			}

			default: {
				break;
			}
		}
	});
	let extraFallbackStoriesUsed = 0;

	// If one of the original three are missing,
	// we'll use the extra fallback stories
	fallbackCustomerStoryList.forEach((story, index) => {
		if (!story && extraFallbackCustomerStoryList[extraFallbackStoriesUsed]) {
			fallbackCustomerStoryList[index] = extraFallbackCustomerStoryList[
				extraFallbackStoriesUsed
			] as CustomerStory;
			extraFallbackStoriesUsed++;
		}
	});

	return fallbackCustomerStoryList;
};

const getShortestColumnIndex = (storyList: StoryGrid): number => {
	let shortestColumnIndex = 0;
	let shortestHeight = (storyList[0] as StoryGridItem).height;
	Object.values(storyList).forEach((story: StoryGridItem, index) => {
		if (shortestHeight > story.height) {
			shortestColumnIndex = index;
			shortestHeight = story.height;
		}
	});

	return shortestColumnIndex;
};

export const sortStoriesByHeight = (
	storiesList: StoryGrid,
	stories: CustomerStory[],
	rows: number,
): StoryGrid => {
	if (Object.keys(storiesList).length === 0) {
		// initialize object
		for (let i = 0; i < rows; i++) {
			storiesList[i] = { stories: [], height: 0 };
		}
	} else if (Object.keys(storiesList).length !== rows || stories.length !== 0) {
		// clear out existing object, and re-intialize it
		for (const key in storiesList) {
			delete storiesList[key];
		}
		for (let i = 0; i < rows; i++) {
			storiesList[i] = { stories: [], height: 0 };
		}
	}
	let shortestColumnIndex = 0;

	stories.forEach((story) => {
		shortestColumnIndex = getShortestColumnIndex(storiesList);
		const storyGridItem = storiesList[shortestColumnIndex] as StoryGridItem;

		if (story.advocateQuotes?.length) {
			storyGridItem.height += sizeMap['lg'];
		} else if (story.storyIcon?.url) {
			storyGridItem.height += sizeMap['md'];
		} else {
			storyGridItem.height += sizeMap['sm'];
		}
		storyGridItem.stories.push(story);
	});
	return storiesList;
};

export const getNumberOfCustomerStoryColumns = (width: number) => {
	if (width > 1024) {
		return 3;
	} else if (width > 768) {
		return 2;
	} else if (width <= 768) {
		return 1;
	} else {
		return 1;
	}
};

/**
 * Viewport width assumed by the server render and by the first client render.
 *
 * WAC's edge cache key does not include the user agent, so a single HTML
 * payload is shared across every device class. The first render must therefore
 * be identical everywhere and the real viewport can only be applied after
 * mount — the same rule `useDeviceInfo` documents.
 *
 * Zero means "not measured yet". It reproduces what the server already does:
 * the Tesseract SSR runtime provides `window` but no `innerWidth`, so every
 * width comparison here was already falsy and the grid already rendered a
 * single column.
 */
export const UNMEASURED_WINDOW_WIDTH = 0;

export const getWindowDimensions = () => {
	// `window` exists during SSR but has no `innerWidth`.
	if (typeof window === 'undefined' || typeof window.innerWidth !== 'number') {
		return { width: UNMEASURED_WINDOW_WIDTH };
	}

	return { width: window.innerWidth };
};

export function useWindowDimensions() {
	// Deliberately does NOT seed from the real viewport. Measuring during render
	// makes the first client render disagree with the server markup, and because
	// the width drives how stories are distributed across masonry columns that
	// disagreement reorders the DOM. React cannot patch that, so it discards the
	// entire server tree and re-renders the whole page client-side.
	const [windowDimensions, setWindowDimensions] = useState({
		width: UNMEASURED_WINDOW_WIDTH,
	});

	useEffect(() => {
		function handleResize() {
			setWindowDimensions(getWindowDimensions());
		}

		// Sync to the real viewport once hydration is done.
		handleResize();

		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	return windowDimensions;
}

export function isContentPlatformFacetAvailable(category: string): boolean {
	return (
		category === 'industry' ||
		category === 'product' ||
		category === 'solution' ||
		category === 'region' ||
		category === 'team size'
	);
}

const defaultCustomerStoryFilterOptionValue: FilterOptions = {
	committedActive: false,
	stagedActive: false,
};

export function getFilterOptions(
	filters: ContentFacetContext[],
	subFilter?: string,
): SubFilterObject {
	const defaulted: SubFilterObject = {};
	filters.forEach((filter) => {
		defaulted[filter.name] = {
			...defaultCustomerStoryFilterOptionValue,
			grouping: subFilter === 'region' || subFilter === 'team size' ? subFilter : undefined,
		};
	});
	return defaulted;
}

export function getFilterEventProps(filterObject: CustomerStoryFilterObject) {
	const committedActive = Object.keys(filterObject).map((key) => {
		const map: string[] = Object.keys(filterObject[key as FilterCategories])
			.map((key2) => {
				const root = filterObject[key as FilterCategories];
				const value = root[key2];
				const category = value?.grouping ?? key;
				return value?.committedActive ? `${category}=${key2}` : '';
			})
			.filter((item) => item?.length);
		return map;
	});

	return committedActive.flat().join('|');
}
